class IVCDiamondFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.ivc-filter__form');
    this.timer = null;
    this.request = null;
    this.onPopState = () => this.updateFromURL(new URL(window.location.href), false);
    window.addEventListener('popstate', this.onPopState);

    const toggle = this.querySelector('.ivc-filter__mobile-toggle');
    toggle?.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });

    this.querySelectorAll('[data-expand]').forEach((button) => {
      button.addEventListener('click', () => {
        const options = button.closest('[data-expandable]');
        const expanded = options.classList.toggle('is-expanded');
        button.querySelector('[data-more-label]').textContent = expanded ? 'View Less' : 'View More';
        button.querySelector('.ivc-filter__more-icon').textContent = expanded ? '−' : '+';
      });
    });

    this.querySelectorAll('[data-range-group]').forEach((group) => this.setupRange(group));
    this.form?.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', () => this.scheduleUpdate(120));
    });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.updateFromURL(this.formURL(), true);
    });
    this.querySelector('.ivc-filter__clear')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.updateFromURL(new URL(event.currentTarget.href), true);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.request?.abort();
    window.removeEventListener('popstate', this.onPopState);
  }

  setupRange(group) {
    const low = group.querySelector('[data-range-min]');
    const high = group.querySelector('[data-range-max]');
    const lowNumber = group.querySelector('[data-number-min]');
    const highNumber = group.querySelector('[data-number-max]');
    const slider = group.querySelector('.ivc-filter__slider');
    if (!low || !high || !lowNumber || !highNumber) return;

    const sync = (source) => {
      let lo = Number(source === lowNumber && lowNumber.value !== '' ? lowNumber.value : low.value);
      let hi = Number(source === highNumber && highNumber.value !== '' ? highNumber.value : high.value);
      if (source === low || source === lowNumber) lo = Math.min(lo, hi);
      if (source === high || source === highNumber) hi = Math.max(hi, lo);
      low.value = String(lo);
      high.value = String(hi);
      if (source === low) lowNumber.value = String(lo);
      if (source === high) highNumber.value = String(hi);
      const max = Number(high.max) || 1;
      slider.style.setProperty('--low', `${(lo / max) * 100}%`);
      slider.style.setProperty('--high', `${(hi / max) * 100}%`);
    };

    [low, high].forEach((input) => {
      input.addEventListener('input', () => sync(input));
      input.addEventListener('change', () => this.scheduleUpdate(180));
    });
    [lowNumber, highNumber].forEach((input) => {
      input.addEventListener('input', () => {
        sync(input);
        this.scheduleUpdate(450);
      });
      input.addEventListener('change', () => this.scheduleUpdate(100));
    });
    sync(low);
  }

  formURL() {
    const url = new URL(this.form.action, window.location.origin);
    const params = new URLSearchParams();
    new FormData(this.form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    params.delete('page');
    url.search = params.toString();
    return url;
  }

  scheduleUpdate(delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.updateFromURL(this.formURL(), true), delay);
  }

  async updateFromURL(url, updateHistory) {
    clearTimeout(this.timer);
    this.request?.abort();
    this.request = new AbortController();
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('.ivc-filter__status');
    if (status) status.textContent = 'Updating inventory…';

    try {
      const response = await fetch(url.toString(), {
        signal: this.request.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextFilters = nextDocument.querySelector('ivc-diamond-filters');
      const nextResults = nextDocument.querySelector('results-list');
      const currentResults = document.querySelector('results-list');
      if (!nextFilters || !nextResults || !currentResults) throw new Error('Filtered content was not returned');

      const wasOpen = this.classList.contains('is-open');
      const importedResults = document.importNode(nextResults, true);
      const importedFilters = document.importNode(nextFilters, true);
      if (wasOpen) {
        importedFilters.classList.add('is-open');
        importedFilters.querySelector('.ivc-filter__mobile-toggle')?.setAttribute('aria-expanded', 'true');
      }

      currentResults.replaceWith(importedResults);
      if (updateHistory && url.toString() !== window.location.href) {
        history.pushState({ ivcFilters: true }, '', url.toString());
      }
      this.replaceWith(importedFilters);
      document.dispatchEvent(new CustomEvent('ivc:filters-updated', { detail: { url: url.toString() } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url.toString());
    }
  }
}

if (!customElements.get('ivc-diamond-filters')) {
  customElements.define('ivc-diamond-filters', IVCDiamondFilters);
}
